import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  listFeedback,
  getFeedback,
  updateFeedback,
  FeedbackListItem,
  FeedbackDetail
} from '../lib/api';

interface Credentials {
  username: string;
  password: string;
}

const STORAGE_KEY = 'fujistyle_admin_creds';

// Encrypt/decrypt for basic obfuscation (not secure, just prevents plain text)
function encodeCredentials(creds: Credentials): string {
  return btoa(JSON.stringify(creds));
}

function decodeCredentials(encoded: string): Credentials | null {
  try {
    return JSON.parse(atob(encoded));
  } catch {
    return null;
  }
}

export function AdminPage() {
  useTranslation(); // Initialize i18n
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // List state
  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocale, setFilterLocale] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load saved credentials on mount - directly restore without verification
  // If credentials are invalid, the API call will return 401 and we'll clear them then
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const creds = decodeCredentials(saved);
      if (creds) {
        setCredentials(creds);
      }
    }
  }, []);

  // Fetch list
  const fetchList = useCallback(async () => {
    if (!credentials) return;

    setLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
        type: filterType || undefined,
        status: filterStatus || undefined,
        q: searchQuery || undefined,
        platform: filterPlatform || undefined
      };

      const response = await listFeedback(params, credentials);

      if (response.ok && response.data) {
        let filteredItems = response.data.items;

        // Client-side locale filter (API doesn't support locale param directly)
        if (filterLocale) {
          filteredItems = filteredItems.filter(item => item.locale === filterLocale);
        }

        // Client-side platform filter as backup
        if (filterPlatform) {
          filteredItems = filteredItems.filter(item => item.platform === filterPlatform);
        }

        setItems(filteredItems);
        setTotal(filterLocale || filterPlatform ? filteredItems.length : response.data.total);
      }
    } catch (error) {
      if ((error as Error).message === 'Unauthorized') {
        setCredentials(null);
        localStorage.removeItem(STORAGE_KEY);
        setLoginError('Session expired');
      }
    } finally {
      setLoading(false);
    }
  }, [credentials, page, pageSize, filterType, filterStatus, filterLocale, filterPlatform, searchQuery]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Fetch detail
  useEffect(() => {
    if (!selectedId || !credentials) return;

    setDetailLoading(true);
    getFeedback(selectedId, credentials)
      .then(response => {
        if (response.ok && response.data) {
          setDetail(response.data);
        }
      })
      .catch(error => {
        if ((error as Error).message === 'Unauthorized') {
          setCredentials(null);
          localStorage.removeItem(STORAGE_KEY);
          setLoginError('Session expired');
        }
      })
      .finally(() => setDetailLoading(false));
  }, [selectedId, credentials]);

  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    const creds = { username, password };

    try {
      const response = await listFeedback({ page: 1, pageSize: 1 }, creds);
      if (response.ok) {
        setCredentials(creds);
        if (rememberMe) {
          localStorage.setItem(STORAGE_KEY, encodeCredentials(creds));
        }
      } else {
        setLoginError('Invalid credentials');
      }
    } catch (error) {
      setLoginError('Invalid credentials');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setCredentials(null);
    localStorage.removeItem(STORAGE_KEY);
    setUsername('');
    setPassword('');
  };

  // Handle status update
  const [updateError, setUpdateError] = useState('');
  const handleStatusUpdate = async (status: string) => {
    if (!selectedId || !credentials) return;
    setUpdateError('');

    try {
      const response = await updateFeedback(selectedId, { status }, credentials);
      if (response.ok) {
        setDetail(prev => prev ? { ...prev, status: status as any } : null);
        fetchList();
      } else {
        setUpdateError('Failed to update status');
      }
    } catch (error) {
      setUpdateError('Failed to update status');
    }
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get locale display name
  const getLocaleName = (locale: string) => {
    const names: Record<string, string> = {
      en: 'English',
      zh: '中文',
      ja: '日本語',
      ko: '한국어',
      de: 'Deutsch',
      fr: 'Français',
      vi: 'Tiếng Việt',
      es: 'Español'
    };
    return names[locale] || locale.toUpperCase();
  };

  // Login form
  if (!credentials) {
    return (
      <div className="container" style={{ maxWidth: '400px', marginTop: '100px' }}>
        <div className="card">
          <div className="header" style={{ paddingBottom: '16px' }}>
            <img src="/logo.png" alt="FUJISTYLE" className="app-logo" style={{ width: '56px', height: '56px' }} />
            <h2 style={{ margin: 0 }}>Admin Login</h2>
          </div>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-input"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>
            {loginError && <div className="form-error" style={{ marginBottom: '16px' }}>{loginError}</div>}
            <button type="submit" className="submit-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">Feedback Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <LanguageSelector />
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <select
          className="filter-select"
          value={filterType}
          onChange={e => { setFilterType(e.target.value); setPage(1); }}
        >
          <option value="">Type: All</option>
          <option value="bug">Bug</option>
          <option value="suggestion">Suggestion</option>
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
        >
          <option value="">Status: All</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
        </select>

        <select
          className="filter-select"
          value={filterLocale}
          onChange={e => { setFilterLocale(e.target.value); setPage(1); }}
        >
          <option value="">Language: All</option>
          <option value="en">English</option>
          <option value="zh">中文</option>
          <option value="ja">日本語</option>
          <option value="ko">한국어</option>
          <option value="de">Deutsch</option>
          <option value="fr">Français</option>
          <option value="vi">Tiếng Việt</option>
          <option value="es">Español</option>
        </select>

        <select
          className="filter-select"
          value={filterPlatform}
          onChange={e => { setFilterPlatform(e.target.value); setPage(1); }}
        >
          <option value="">Platform: All</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
          <option value="web">Web</option>
        </select>

        <input
          type="text"
          className="search-input"
          placeholder="Search title or description..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchList()}
        />
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
        Total: {total} feedback items
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ref Code</th>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Language</th>
                  <th>Platform</th>
                  <th>Files</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.ref_code}</td>
                    <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{formatDate(item.created_at)}</td>
                    <td>
                      <span className={`status-badge ${item.type}`}>
                        {item.type === 'bug' ? '🐛 Bug' : '💡 Idea'}
                      </span>
                    </td>
                    <td className="table-title">{item.title}</td>
                    <td>
                      <span className={`status-badge ${item.status}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{getLocaleName(item.locale)}</td>
                    <td>{item.platform || '-'}</td>
                    <td>{item.attachments_count > 0 ? `📎 ${item.attachments_count}` : '-'}</td>
                    <td>
                      <button
                        className="view-btn"
                        onClick={() => setSelectedId(item.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No feedback found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {Math.min((page - 1) * pageSize + 1, total)} - {Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="pagination-btns">
            <button
              className="pagination-btn"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </button>
            <span style={{ padding: '0 12px', alignSelf: 'center' }}>Page {page}</span>
            <button
              className="pagination-btn"
              disabled={page * pageSize >= total}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedId && (
        <div className="modal-overlay" onClick={() => { setSelectedId(null); setUpdateError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {detail?.ref_code || 'Loading...'}
              </h3>
              <button className="modal-close" onClick={() => { setSelectedId(null); setUpdateError(''); }}>×</button>
            </div>

            <div className="modal-body">
              {detailLoading ? (
                <div className="loading">Loading...</div>
              ) : detail ? (
                <>
                  {/* Header Info */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #eee'
                  }}>
                    <span className={`status-badge ${detail.type}`}>
                      {detail.type === 'bug' ? '🐛 Bug' : '💡 Suggestion'}
                    </span>
                    <span className={`status-badge ${detail.status}`}>
                      {detail.status.replace('_', ' ')}
                    </span>
                    <span className="status-badge" style={{ background: '#f0f0f0' }}>
                      {getLocaleName(detail.locale)}
                    </span>
                    {detail.platform && (
                      <span className="status-badge" style={{ background: '#f0f0f0' }}>
                        {detail.platform === 'ios' ? '🍎' : detail.platform === 'android' ? '🤖' : '🌐'} {detail.platform}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <div className="detail-row">
                    <div className="detail-label">Title</div>
                    <div className="detail-value" style={{ fontSize: '18px', fontWeight: '600' }}>
                      {detail.title}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="detail-row">
                    <div className="detail-label">Description</div>
                    <div className="detail-value" style={{
                      whiteSpace: 'pre-wrap',
                      background: '#f9f9f9',
                      padding: '16px',
                      borderRadius: '8px',
                      lineHeight: '1.6'
                    }}>
                      {detail.description}
                    </div>
                  </div>

                  {/* User Contact */}
                  {detail.email && (
                    <div className="detail-row">
                      <div className="detail-label">User Email</div>
                      <div className="detail-value">
                        <a href={`mailto:${detail.email}`} style={{ color: '#0066cc' }}>
                          {detail.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Device Info */}
                  {(detail.app_version || detail.device_model || detail.os_version) && (
                    <div className="detail-row">
                      <div className="detail-label">Device Info</div>
                      <div className="detail-value" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {detail.app_version && <span>App: v{detail.app_version}</span>}
                        {detail.device_model && <span>Device: {detail.device_model}</span>}
                        {detail.os_version && <span>OS: {detail.os_version}</span>}
                      </div>
                    </div>
                  )}

                  {/* Bug-specific fields */}
                  {detail.type === 'bug' && (
                    <div style={{
                      background: '#fff8f0',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '12px', color: '#c65d00' }}>
                        🐛 Bug Details
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {detail.frequency && (
                          <div>
                            <span style={{ color: '#666' }}>Frequency: </span>
                            <span>{detail.frequency}</span>
                          </div>
                        )}
                        {detail.severity && (
                          <div>
                            <span style={{ color: '#666' }}>Severity: </span>
                            <span style={{
                              color: detail.severity === 'blocker' ? '#dc2626' :
                                     detail.severity === 'major' ? '#ea580c' : '#666'
                            }}>
                              {detail.severity}
                            </span>
                          </div>
                        )}
                      </div>
                      {detail.steps && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ color: '#666', marginBottom: '4px' }}>Steps to Reproduce:</div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{detail.steps}</div>
                        </div>
                      )}
                      {detail.expected && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ color: '#666', marginBottom: '4px' }}>Expected:</div>
                          <div>{detail.expected}</div>
                        </div>
                      )}
                      {detail.actual && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ color: '#666', marginBottom: '4px' }}>Actual:</div>
                          <div>{detail.actual}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Suggestion-specific fields */}
                  {detail.type === 'suggestion' && (detail.category || detail.priority || detail.scenario) && (
                    <div style={{
                      background: '#f0fff4',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '12px', color: '#16a34a' }}>
                        💡 Suggestion Details
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {detail.category && (
                          <div>
                            <span style={{ color: '#666' }}>Category: </span>
                            <span>{detail.category}</span>
                          </div>
                        )}
                        {detail.priority && (
                          <div>
                            <span style={{ color: '#666' }}>Priority: </span>
                            <span style={{
                              color: detail.priority === 'urgent' ? '#dc2626' :
                                     detail.priority === 'important' ? '#ea580c' : '#666'
                            }}>
                              {detail.priority.replace('_', ' ')}
                            </span>
                          </div>
                        )}
                      </div>
                      {detail.scenario && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ color: '#666', marginBottom: '4px' }}>Use Case:</div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{detail.scenario}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attachments */}
                  {detail.attachments.length > 0 && (
                    <div className="detail-row">
                      <div className="detail-label">Attachments ({detail.attachments.length})</div>
                      <div className="attachment-preview">
                        {detail.attachments.map(att => (
                          <img
                            key={att.id}
                            src={att.preview_url}
                            alt={att.original_name || 'attachment'}
                            className="attachment-img"
                            style={{ width: '120px', height: '120px' }}
                            onClick={() => window.open(att.preview_url, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="detail-row" style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                    <div style={{ display: 'flex', gap: '24px', fontSize: '13px', color: '#666' }}>
                      <span>Created: {formatDate(detail.created_at)}</span>
                      <span>Updated: {formatDate(detail.updated_at)}</span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <div className="modal-footer" style={{ justifyContent: 'space-between', flexDirection: 'column', gap: '12px' }}>
              {updateError && (
                <div style={{ color: '#dc2626', fontSize: '14px', width: '100%' }}>{updateError}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Status:</span>
                  {['new', 'in_progress', 'resolved', 'ignored'].map(status => (
                    <button
                      key={status}
                      className={`status-badge ${status}`}
                      style={{
                        cursor: 'pointer',
                        border: detail?.status === status ? '2px solid #000' : 'none',
                        opacity: detail?.status === status ? 1 : 0.7
                      }}
                      onClick={() => handleStatusUpdate(status)}
                    >
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              {detail?.email && (
                <a
                  href={`mailto:${detail.email}?subject=Re: ${detail.ref_code} - ${detail.title}`}
                  className="view-btn"
                  style={{ textDecoration: 'none' }}
                >
                  Reply to User
                </a>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
